import enums from '@happylife-a/enums';
import firebase from '@happylife-a/firebase';
import utils from '@happylife-a/utils';
import { mapOnClients } from '../core/http';
import * as helpers from '../helpers';

let storageKeyPrefix = null;
export function setAuthStorageKeyPrefix(prefix) {
  storageKeyPrefix = prefix;
}

function prefixedKey(...keys) {
  return ['auth', storageKeyPrefix, ...keys].filter(Boolean).join('::');
}

function getSessionKeyAccessToken() {
  return prefixedKey('aws_cognito', 'access_token');
}
function getSessionKeyRefreshToken() {
  return prefixedKey('aws_cognito', 'refresh_token');
}
function getSessionKeyUserIdentifier() {
  return prefixedKey('user', 'identifier');
}
function getSessionKeyUserObject() {
  return prefixedKey('user', 'object');
}

function setAuthorizationHeader(accessTokenOrGuestIdentifier) {
  const value = accessTokenOrGuestIdentifier
    ? utils.helpers.user.buildAuthorizationHeader(accessTokenOrGuestIdentifier)
    : '';

  mapOnClients(({ httpClient }) => {
    httpClient.defaults.headers.common.Authorization = value;
  });
}

export async function clearTokenStorage() {
  setAuthorizationHeader(null);

  await revokeTokens();
  await helpers.storage.removeItem(getSessionKeyAccessToken());
  await helpers.storage.removeItem(getSessionKeyRefreshToken());
  await helpers.storage.removeItem(getSessionKeyUserIdentifier());
  await helpers.storage.removeItem(getSessionKeyUserObject());
}

export async function processTokenResponse(identifier, response, clearTokenOnProblem = true) {
  const { accessToken, refreshToken } = response || {};
  if (!accessToken) {
    if (clearTokenOnProblem) {
      await clearTokenStorage();
    }

    return;
  }

  setAuthorizationHeader(accessToken);
  await setAccessToken(accessToken);

  if (refreshToken) {
    await setRefreshToken(refreshToken);
  }
  if (identifier) {
    await setIdentifier(identifier);
  }
}

export function sanitizeIdentifier(identifier, role) {
  if (typeof identifier !== 'string') {
    return identifier;
  }

  identifier = identifier.trim();
  if (!identifier) {
    return identifier;
  }
  if (identifier.includes('@')) {
    return identifier.toLowerCase();
  }

  const isCustomer = role?.toLowerCase() === enums.user.UserRoleEnum.CUSTOMER;
  if (!isCustomer) {
    return identifier;
  }

  // @INFO: some users already registered with uppercase nicknames.
  const alreadyRegisteredIdentifiersList = [
    'Lilbroyan',
    'Test_aa',
    'Ttt',
    'Doctor',
    'Doc',
    'Elen',
    'Amalie',
    'Ellen',
    'Alla',
    'Susya',
    'Lusine',
    'Dgdgd',
    'Armine',
    'SevakMinasyan',
    'Gagik',
    'Abovyannie',
    'Erika',
    'Vahe',
    'Levon',
    'Ruz',
    'Gevjan',
    'Denis',
    'Roman',
    'Ani',
  ];

  if (!alreadyRegisteredIdentifiersList.includes(identifier)) {
    identifier = identifier.toLowerCase();
  }

  return identifier;
}

export async function setAccessToken(accessToken) {
  await helpers.storage.setItem(getSessionKeyAccessToken(), accessToken);
}
export async function getAccessToken() {
  const accessToken = await helpers.storage.getItem(getSessionKeyAccessToken());
  return accessToken;
}

export async function setRefreshToken(refreshToken) {
  await helpers.storage.setItem(getSessionKeyRefreshToken(), refreshToken);
}
export async function getRefreshToken() {
  const refreshToken = await helpers.storage.getItem(getSessionKeyRefreshToken());
  return refreshToken;
}

export async function setIdentifier(UserIdentifier) {
  await helpers.storage.setItem(getSessionKeyUserIdentifier(), UserIdentifier);
}
export async function getIdentifier() {
  const UserIdentifier = await helpers.storage.getItem(getSessionKeyUserIdentifier());
  return UserIdentifier;
}

export async function setLoggedUser(loggedUser, guestAccessToken) {
  if (!loggedUser) {
    return;
  }

  let userId = loggedUser.id;
  let userNickname = loggedUser.nickname;

  if (loggedUser.isGuest) {
    userId = null;
    userNickname = null;
  }

  if (loggedUser.isGuest && guestAccessToken) {
    const guestIdentifier = utils.helpers.user.buildGuestIdentifier(guestAccessToken);

    setAuthorizationHeader(guestIdentifier);
    await setIdentifier(guestIdentifier);
  }

  await helpers.storage.setItem(getSessionKeyUserObject(), loggedUser);

  if (userId) {
    await firebase.libraries.analytics.user.setId(userId);
    await firebase.libraries.analytics.user.setProperties({
      nickname: userNickname,
      id: userId,
    });
  }
}
export async function getLoggedUser() {
  const loggedUser = await helpers.storage.getItem(getSessionKeyUserObject());
  return loggedUser;
}

export async function revokeTokens() {
  await helpers.storage.removeItem(getSessionKeyAccessToken());
  await helpers.storage.removeItem(getSessionKeyRefreshToken());
  await helpers.storage.removeItem(getSessionKeyUserIdentifier());

  await helpers.storage.removeItem(getSessionKeyUserObject());
}

export async function getTokens() {
  const identifier = await getIdentifier();
  const refreshToken = await getRefreshToken();
  const accessToken = await getAccessToken();

  return {
    identifier: identifier,
    refreshToken: refreshToken,
    accessToken: accessToken,
  };
}

export async function initialize() {
  const { identifier, refreshToken, accessToken } = await getTokens();
  if (!identifier) {
    return false;
  }

  if (!refreshToken || !accessToken) {
    // @INFO: if accessToken and refreshToken are missing and identifier exists,
    //        than it means currently we have guest user
    const isGuest = utils.helpers.user.isGuestIdentifier(identifier);
    if (isGuest) {
      setAuthorizationHeader(identifier);
    }

    return isGuest;
  }

  // initialize tokens
  const tokens = {
    accessToken: accessToken,
    refreshToken: refreshToken,
  };

  await processTokenResponse(identifier, tokens, false);
  return true;
}

export async function checkIsLoggedIn() {
  const accessToken = await getAccessToken();
  return !!accessToken;
}
